import { useBunja } from 'bunja/react';
import { useAtom, useAtomValue } from 'jotai';
import { useTranslations } from 'next-intl';
import { memo } from 'react';

import { CommunityFeedTabType } from '@flipster/apis/v1/community/types';
import { NoFeedYet } from '@flipster/features/src/shared/community-feed/no-feed-yet';
import { createSingleChoice } from '@flipster/react/design-system/chip';
import { useMeasure } from '@flipster/react/misc/browser/measure';
import { Styles, css } from '@flipster/styled-system/css';
import { stack } from '@flipster/styled-system/patterns/stack';
import segment from '@flipster/utils/segment';
import { CommunityEvent } from '@flipster/utils/segment/events/community';

import { fomoBannerBunja } from '../../../fomo-banner/bunja';
import { communityFeedBunja } from './bunja';
import { FeedList } from './feed-list';
import { FeedPost } from './feed-post';
import { SymbolGeneratedContents } from './generated-contents';
import { LivePnlShoutout } from './generated-contents/live-pnl-shoutout';

const Choice = createSingleChoice<CommunityFeedTabType>();

const twoColumnMinWidth = 960;

const getNoFeedYetContent = (tab: CommunityFeedTabType, hasAnyTradeRecord?: boolean) => {
  if (tab === 'POST') {
    if (hasAnyTradeRecord) {
      return { title: 'No posts yet', description: 'Be the first to post.' };
    }
    return { title: 'No posts yet', description: 'You can comment after trading this symbol.' };
  }
  return { title: 'No trades yet', description: 'Share your trades to connect with others.' };
};

interface CommunityFeedProps {
  css?: Styles;
}

const CommunityFeed = memo(({ css: cssProps }: CommunityFeedProps) => {
  const t = useTranslations();
  useBunja(fomoBannerBunja);
  const { symbol, communityFeedAtom, symbolLeaderboardAtom, isPendingAtom, communityFeedTabAtom, fetchNextPage } =
    useBunja(communityFeedBunja);
  const feed = useAtomValue(communityFeedAtom);
  const symbolLeaderboard = useAtomValue(symbolLeaderboardAtom);
  const isPending = useAtomValue(isPendingAtom);
  const [tab, setTab] = useAtom(communityFeedTabAtom);

  const [sectionRef, { width }] = useMeasure<HTMLElement>();
  // Container-based threshold (panel is resizable, viewport breakpoint is no longer accurate)
  const isWide = width >= twoColumnMinWidth;

  const isShowFeed = feed && feed.length > 0;
  const isShowSymbolLeaderboard = symbolLeaderboard && symbolLeaderboard.rankings.length > 0;

  if (symbol == null || isPending) return null;

  return (
    <section
      ref={sectionRef}
      className={css(
        {
          display: 'grid',
          gridTemplateAreas: isWide
            ? `"left generated"`
            : `
              "post"
              "tab"
              "feed"
            `,
          gridTemplateRows: isWide ? '1fr' : undefined,
          gridTemplateColumns: isWide ? 'minmax(0, 600px) 360px' : '100%',
          columnGap: 64,
          rowGap: 24,
          overflowY: 'scroll',
        },
        cssProps,
      )}
    >
      {/* Left column: display:contents at narrow (children participate in parent grid),
          flex column at wide (scrolls with parent section) */}
      <div
        className={css({
          display: isWide ? 'flex' : 'contents',
          flexDir: isWide ? 'column' : undefined,
          gridArea: isWide ? 'left' : undefined,
        })}
      >
        <FeedPost
          css={{
            gridArea: 'post',
            minW: 0,
            pb: 24,
            borderBottomWidth: '1px',
            borderBottomStyle: 'solid',
            borderColor: 'border.default.level-1',
          }}
        />
        <Choice
          value={tab}
          onChange={(value) => {
            setTab(value);
            segment.track(CommunityEvent.FeedTypeClick, { feed_type: value === 'POST' ? 'feed' : 'trades' });
          }}
          css={{ gridArea: 'tab', py: 24 }}
        >
          <Choice.Chip value='POST' label={t('Autogen_common.r251126.s_Feed')} />
          <Choice.Chip value='TRADE' label={t('Autogen_common.r251126.s_Trades')} />
        </Choice>
        <div className={css({ gridArea: { base: 'feed' } })}>
          {isShowFeed && (
            <FeedList
              feed={feed}
              showGenerated={!isWide && tab === 'POST'}
              fetchNextPage={fetchNextPage}
              itemCss={css.raw({
                py: 24,
                '&:first-child': {
                  pt: 0,
                },
                '&:not(:last-child)': {
                  borderBottomWidth: '1px',
                  borderBottomStyle: 'solid',
                  borderColor: 'border.default.level-1',
                },
              })}
            />
          )}
          {!isShowFeed && (
            <div className={css({ margin: '0 auto' })}>
              <NoFeedYet {...getNoFeedYetContent(tab, isShowSymbolLeaderboard)} />
            </div>
          )}
        </div>
      </div>
      {/* Right column: generated contents, scrollable when container is wide enough */}
      {isWide && (
        <div
          className={css(
            stack.raw({
              gap: 24,
              gridArea: 'generated',
              alignSelf: 'flex-start',
            }),
          )}
        >
          <SymbolGeneratedContents />
          <LivePnlShoutout
            css={{
              '&:not(:first-child)': {
                borderTopWidth: '1px',
                borderTopStyle: 'solid',
                borderColor: 'border.default.level-1',
                pt: 24,
              },
              pb: 24,
            }}
          />
        </div>
      )}
    </section>
  );
});

export default CommunityFeed;
